import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createExpenseRequest,
  deleteExpenseRequest,
  getExpensesRequest,
  updateExpenseRequest,
} from "../../api/expenseApi";
import { CreateExpenseRequest } from "../../models/RequestModels/CreateExpenseRequest";
import { UpdateExpenseRequest } from "../../models/RequestModels/UpdateExpenseRequest";
import { ExpenseResponse } from "../../models/ResponseModels/ExpenseResponse";
import {
  getApiErrorMessage,
  getApiErrorPayload,
} from "../../utils/helperFunctions/apiErrorHelper";

interface FetchExpensesParams {
  from?: string;
  until?: string;
  property_id?: number;
  unit_id?: number;
  currency?: string;
}

interface UpdateExpensePayload {
  id: number;
  body: UpdateExpenseRequest;
}

interface ExpenseState {
  expenses: ExpenseResponse[];
  loading: boolean;
  error: string | null;
}

const initialState: ExpenseState = {
  expenses: [],
  loading: false,
  error: null,
};

export const fetchExpenses = createAsyncThunk(
  "expenses/fetchExpenses",
  async (params: FetchExpensesParams | undefined, { rejectWithValue }) => {
    try {
      const response = await getExpensesRequest(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const createExpense = createAsyncThunk(
  "expenses/createExpense",
  async (body: CreateExpenseRequest, { rejectWithValue }) => {
    try {
      const response = await createExpenseRequest(body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const updateExpense = createAsyncThunk(
  "expenses/updateExpense",
  async ({ id, body }: UpdateExpensePayload, { rejectWithValue }) => {
    try {
      const response = await updateExpenseRequest(id, body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const deleteExpense = createAsyncThunk(
  "expenses/deleteExpense",
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteExpenseRequest(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

const expenseSlice = createSlice({
  name: "expenses",
  initialState,
  reducers: {
    clearExpenseError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExpenses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExpenses.fulfilled, (state, action) => {
        state.loading = false;
        state.expenses = action.payload;
      })
      .addCase(fetchExpenses.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to load expenses.",
        );
      })

      .addCase(createExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.expenses.unshift(action.payload);
      })
      .addCase(createExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to create expense.",
        );
      })

      .addCase(updateExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.expenses = state.expenses.map((expense) =>
          expense.id === action.payload.id ? action.payload : expense,
        );
      })
      .addCase(updateExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to update expense.",
        );
      })

      .addCase(deleteExpense.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteExpense.fulfilled, (state, action) => {
        state.loading = false;
        state.expenses = state.expenses.filter(
          (expense) => expense.id !== action.payload,
        );
      })
      .addCase(deleteExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to delete expense.",
        );
      });
  },
});

export const { clearExpenseError } = expenseSlice.actions;

export default expenseSlice.reducer;
